'use client';

import { SpecificAllocation } from '@/app/models/specificAllocation';
import {
  useDeleteSpecificAllocationMutation,
  useFindAllSpecificAllocationsGroupedQuery,
} from '@/app/services/specificAllocationApi';
import TruncatedTextWithTooltip from '@/app/utils/truncatedTextWithTooltip';
import ActionMenu, { ItemAction } from '@/components/common/ActionMenu';
import DataTable, { bodyCellSx, Column } from '@/components/common/DataTable';
import CancelOrConfirmDialog from '@/components/common/dialogs/CancelOrConfirmDialog';
import { TableCell } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface SpecificAllocationTableProps {
  selectedAllocation: string | null;
  setSelectedAllocation: (id: string | null) => void;
  setOpenEdit: (open: boolean) => void;
}

export default function SpecificAllocationTable({
  selectedAllocation,
  setSelectedAllocation,
  setOpenEdit,
}: SpecificAllocationTableProps) {
  const { data: specificAllocations } = useFindAllSpecificAllocationsGroupedQuery();
  const { t } = useTranslation(['specificAllocation', 'common']);
  const [excludeDialog, setExcludeDialog] = useState(false);

  const [deleteSpecificAllocation] = useDeleteSpecificAllocationMutation();

  const handleDelete = async () => {
    if (!selectedAllocation) return;
    toast.info(t('exluding.specific.allocation'));
    await deleteSpecificAllocation(selectedAllocation);
    toast.success(t('specific.allocation.excluded'));
    setExcludeDialog(false);
    setSelectedAllocation(null);
  };

  const columns: Column[] = [
    { key: 'project', label: t('common:project') },
    { key: 'collaborators', label: t('allocated.collaborators') },
    { key: 'actions', label: t('common:actions'), align: 'right' },
  ];

  const renderRow = (allocation: SpecificAllocation) => (
    <>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip
          value={`${allocation.project.code} - ${allocation.project.name}`}
          size={80}
          sx={bodyCellSx}
        />
      </TableCell>
      <TableCell sx={bodyCellSx}>
        <TruncatedTextWithTooltip value={allocation.badges.join(', ')} size={80} sx={bodyCellSx} />
      </TableCell>
      <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }}>
        <ActionMenu>
          <ItemAction
            text={t('common:edit') as string}
            actionType="edit"
            onClick={() => {
              setSelectedAllocation(allocation.project.code);
              setOpenEdit(true);
            }}
          />
          <ItemAction
            text={t('common:delete') as string}
            actionType="delete"
            onClick={() => {
              setSelectedAllocation(allocation.project.code);
              setExcludeDialog(true);
            }}
          />
        </ActionMenu>
      </TableCell>
    </>
  );

  // Use project ID as the unique key
  const getRowKey = (allocation: SpecificAllocation) => allocation.project.id;

  return (
    <>
      <DataTable
        columns={columns}
        data={specificAllocations || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage={t('common:no.results.found')}
        size="small"
        stickyHeader
      />

      <CancelOrConfirmDialog
        title={t('exclude.specific.allocation')}
        contentText={t('confirm.exclude.specific.allocation') as string}
        open={excludeDialog}
        onClose={() => setExcludeDialog(false)}
        onConfirm={() => {
          handleDelete();
        }}
      />
    </>
  );
}
